import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area, CartesianGrid,
  RadialBarChart, RadialBar
} from 'recharts';

// ── Palette ──────────────────────────────────────────────
const COLORS = {
  blue: '#3b82f6',
  cyan: '#06b6d4',
  emerald: '#10b981',
  purple: '#8b5cf6',
  amber: '#f59e0b',
  rose: '#f43f5e',
  indigo: '#6366f1',
  teal: '#14b8a6'
};
const PIE_PALETTE = [COLORS.blue, COLORS.emerald, COLORS.purple, COLORS.amber, COLORS.rose, COLORS.cyan, COLORS.indigo, COLORS.teal];

// ── Helpers ──────────────────────────────────────────────
const toNum = v => { const n = Number(v ?? 0); return Number.isFinite(n) ? n : 0; };
const fmtMoney = n => Number(n || 0).toLocaleString();
const monthName = m => {
  const [y, mo] = m.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(mo) - 1]} ${y.slice(2)}`;
};

// ── Custom Tooltip ───────────────────────────────────────
const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-slate-300 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: p.color }}>
          {p.name}: {fmtMoney(p.value)}{suffix}
        </p>
      ))}
    </div>
  );
};

// ── Tab button ───────────────────────────────────────────
const Tab = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${active
        ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
      }`}
  >
    <span>{icon}</span>
    {label}
  </button>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const MaintenanceAnalytics = ({ token, vehicles = [], maintenanceLogs = {} }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // ── Flatten all logs for the owner's vehicles ──
  const allLogs = useMemo(() => {
    const logs = [];
    Object.entries(maintenanceLogs).forEach(([vehicleId, vLogs]) => {
      if (!Array.isArray(vLogs)) return;
      const vehicle = vehicles.find(v => v._id === vehicleId);
      vLogs.forEach(log => {
        logs.push({ ...log, vehicleId, vehiclePlate: vehicle?.plateNumber, vehicleBrand: vehicle?.brand, vehicleModel: vehicle?.model });
      });
    });
    return logs;
  }, [maintenanceLogs, vehicles]);

  // ── Monthly cost trend (last 12 months) ──
  const monthlyData = useMemo(() => {
    const map = {};
    allLogs.forEach(log => {
      const d = log.date || log.performedDate || log.createdAt;
      if (!d) return;
      const key = new Date(d).toISOString().slice(0, 7);
      if (!map[key]) map[key] = { month: key, cost: 0, count: 0, parts: 0, labor: 0 };
      map[key].cost += toNum(log.totalCost);
      map[key].parts += toNum(log.partsCost);
      map[key].labor += toNum(log.laborCost);
      map[key].count += 1;
    });
    return Object.values(map)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-12)
      .map(m => ({ ...m, month: monthName(m.month) }));
  }, [allLogs]);

  // ── Cost by vehicle ──
  const costByVehicle = useMemo(() => {
    const map = {};
    allLogs.forEach(log => {
      const key = log.vehiclePlate || log.vehicleId;
      if (!map[key]) map[key] = { name: key, cost: 0, count: 0 };
      map[key].cost += toNum(log.totalCost);
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.cost - a.cost);
  }, [allLogs]);

  // ── Service type breakdown ──
  const serviceBreakdown = useMemo(() => {
    const map = {};
    allLogs.forEach(log => {
      const reason = (log.serviceReason || log.description || 'Other').trim();
      const key = reason.length > 25 ? reason.slice(0, 22) + '…' : reason;
      if (!map[key]) map[key] = { name: key, value: 0, count: 0 };
      map[key].value += toNum(log.totalCost);
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [allLogs]);

  // ── Summary stats ──
  const stats = useMemo(() => {
    const total = allLogs.reduce((s, l) => s + toNum(l.totalCost), 0);
    const parts = allLogs.reduce((s, l) => s + toNum(l.partsCost), 0);
    const labor = allLogs.reduce((s, l) => s + toNum(l.laborCost), 0);
    const count = allLogs.length;
    const avgPerService = count ? total / count : 0;

    // Most expensive month
    const mostExpMonth = monthlyData.reduce((best, m) => m.cost > (best?.cost || 0) ? m : best, null);

    // Most serviced vehicle
    const mostServiced = costByVehicle.length ? costByVehicle[0] : null;

    return { total, parts, labor, count, avgPerService, mostExpMonth, mostServiced };
  }, [allLogs, monthlyData, costByVehicle]);

  // ── Parts/Labor split for pie ──
  const partsLaborData = useMemo(() => {
    const data = [];
    if (stats.parts > 0) data.push({ name: 'Parts', value: stats.parts });
    if (stats.labor > 0) data.push({ name: 'Labor', value: stats.labor });
    const other = stats.total - stats.parts - stats.labor;
    if (other > 0) data.push({ name: 'Other', value: other });
    return data;
  }, [stats]);

  // ── Vehicle health overview ──
  const vehicleHealth = useMemo(() => {
    return vehicles.map(v => {
      const odo = toNum(v.odometerReading);
      const nextOil = toNum(v.nextOilChangeOdometer);
      const remaining = nextOil - odo;
      const interval = v.oilChangeIntervalKm || 5000;
      const pct = nextOil > 0 ? Math.min(100, Math.max(0, ((interval - remaining) / interval) * 100)) : 0;
      return {
        name: v.plateNumber || `${v.brand} ${v.model}`,
        health: Math.max(0, 100 - pct),
        fill: pct > 90 ? COLORS.rose : pct > 70 ? COLORS.amber : COLORS.emerald
      };
    });
  }, [vehicles]);

  // ── No data state ──
  if (vehicles.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl mb-3 block">📊</span>
        <p className="text-slate-400">Add vehicles and log maintenance to see analytics</p>
      </div>
    );
  }

  const hasLogs = allLogs.length > 0;

  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Tab icon="📈" label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
        <Tab icon="💰" label="Costs" active={activeTab === 'costs'} onClick={() => setActiveTab('costs')} />
        <Tab icon="🔧" label="Services" active={activeTab === 'services'} onClick={() => setActiveTab('services')} />
        <Tab icon="🚗" label="Vehicles" active={activeTab === 'vehicles'} onClick={() => setActiveTab('vehicles')} />
      </div>

      {/* ════════════ OVERVIEW TAB ════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard icon="💰" label="Total Spent" value={`${fmtMoney(stats.total)} FCFA`} color="from-blue-600/20 to-blue-800/20" />
            <SummaryCard icon="🔧" label="Services Done" value={stats.count} color="from-emerald-600/20 to-emerald-800/20" />
            <SummaryCard icon="📊" label="Avg / Service" value={`${fmtMoney(Math.round(stats.avgPerService))} FCFA`} color="from-purple-600/20 to-purple-800/20" />
            <SummaryCard icon="🚗" label="Vehicles" value={vehicles.length} color="from-amber-600/20 to-amber-800/20" />
          </div>

          {/* Cost Trend Chart */}
          {hasLogs && monthlyData.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <span>📈</span> Monthly Cost Trend
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip suffix=" FCFA" />} />
                  <Area type="monotone" dataKey="cost" name="Cost" stroke={COLORS.blue} fill="url(#costGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {!hasLogs && (
            <div className="text-center py-10 bg-slate-700/20 rounded-xl border border-slate-700/30">
              <span className="text-3xl mb-2 block">📋</span>
              <p className="text-slate-400 text-sm">No maintenance records yet</p>
              <p className="text-slate-500 text-xs mt-1">Your analytics will appear here after your first service</p>
            </div>
          )}
        </div>
      )}

      {/* ════════════ COSTS TAB ════════════ */}
      {activeTab === 'costs' && (
        <div className="space-y-6">
          {hasLogs ? (
            <>
              {/* Parts vs Labor */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span>🥧</span> Parts vs Labor Split
                  </h3>
                  {partsLaborData.length > 0 ? (
                    <div className="flex items-center justify-center">
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={partsLaborData}
                            cx="50%" cy="50%"
                            innerRadius={50} outerRadius={85}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {partsLaborData.map((_, i) => (
                              <Cell key={i} fill={PIE_PALETTE[i]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip suffix=" FCFA" />} />
                          <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            formatter={(val) => <span className="text-slate-300 text-xs">{val}</span>}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-8">No cost breakdown data available</p>
                  )}
                </div>

                {/* Monthly Parts vs Labor stacked bar chart */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span>📊</span> Monthly Breakdown
                  </h3>
                  {monthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} />
                        <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip content={<CustomTooltip suffix=" FCFA" />} />
                        <Bar dataKey="parts" name="Parts" fill={COLORS.blue} stackId="a" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="labor" name="Labor" fill={COLORS.emerald} stackId="a" radius={[4, 4, 0, 0]} />
                        <Legend
                          verticalAlign="bottom"
                          iconType="circle"
                          formatter={(val) => <span className="text-slate-300 text-xs">{val}</span>}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-8">No monthly data yet</p>
                  )}
                </div>
              </div>

              {/* Cost by Vehicle Table */}
              {costByVehicle.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                    <span>🚗</span> Spending by Vehicle
                  </h3>
                  <div className="space-y-2">
                    {costByVehicle.map((v, i) => {
                      const maxCost = costByVehicle[0]?.cost || 1;
                      const pct = (v.cost / maxCost) * 100;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-slate-300 text-sm w-28 truncate font-medium">{v.name}</span>
                          <div className="flex-1 h-6 bg-slate-700/50 rounded-full overflow-hidden relative">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: `${pct}%`,
                                background: `linear-gradient(90deg, ${PIE_PALETTE[i % PIE_PALETTE.length]}, ${PIE_PALETTE[(i + 1) % PIE_PALETTE.length]})`
                              }}
                            />
                            <span className="absolute inset-0 flex items-center pl-3 text-xs text-white font-medium">
                              {fmtMoney(v.cost)} FCFA · {v.count} service{v.count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {/* ════════════ SERVICES TAB ════════════ */}
      {activeTab === 'services' && (
        <div className="space-y-6">
          {hasLogs ? (
            <>
              {/* Service Type Pie */}
              {serviceBreakdown.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                      <span>🔧</span> Service Types
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={serviceBreakdown}
                          cx="50%" cy="50%"
                          outerRadius={90}
                          innerRadius={40}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {serviceBreakdown.map((_, i) => (
                            <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip suffix=" FCFA" />} />
                        <Legend
                          verticalAlign="bottom"
                          iconType="circle"
                          formatter={(val) => <span className="text-slate-300 text-xs">{val}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Service frequency bars */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                      <span>📋</span> Service Frequency
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart layout="vertical" data={serviceBreakdown.slice(0, 6)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis type="number" stroke="#94a3b8" fontSize={10} />
                        <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={10} width={100} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Times" fill={COLORS.cyan} radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Recent Services Timeline */}
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <span>🕐</span> Recent Services
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {allLogs
                    .filter(l => l.date || l.performedDate || l.createdAt)
                    .sort((a, b) => new Date(b.date || b.performedDate || b.createdAt) - new Date(a.date || a.performedDate || a.createdAt))
                    .slice(0, 10)
                    .map((log, i) => {
                      const d = log.date || log.performedDate || log.createdAt;
                      return (
                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-700/20 rounded-xl border border-slate-700/30 hover:bg-slate-700/40 transition-colors">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_PALETTE[i % PIE_PALETTE.length] }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">{log.serviceReason || log.description || 'Service'}</p>
                            <p className="text-xs text-slate-400">{log.vehiclePlate || 'N/A'} · {new Date(d).toLocaleDateString()}</p>
                          </div>
                          <span className="text-sm font-semibold text-emerald-400 flex-shrink-0">
                            {toNum(log.totalCost) > 0 ? `${fmtMoney(log.totalCost)} FCFA` : '—'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {/* ════════════ VEHICLES TAB ════════════ */}
      {activeTab === 'vehicles' && (
        <div className="space-y-6">
          {/* Vehicle health radial bars */}
          {vehicleHealth.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <span>❤️</span> Vehicle Health (Oil Change Status)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vehicleHealth.map((v, i) => (
                  <div key={i} className="bg-slate-700/20 rounded-xl border border-slate-700/30 p-4 flex items-center gap-3">
                    <div className="w-20 h-20 relative flex-shrink-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%" data={[v]} startAngle={90} endAngle={-270}>
                          <RadialBar background={{ fill: '#334155' }} dataKey="health" cornerRadius={8} />
                        </RadialBarChart>
                      </ResponsiveContainer>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                        {Math.round(v.health)}%
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{v.name}</p>
                      <p className={`text-xs font-medium ${v.health >= 50 ? 'text-emerald-400' : v.health >= 20 ? 'text-amber-400' : 'text-red-400'}`}>
                        {v.health >= 50 ? '✓ Good' : v.health >= 20 ? '⚠ Attention' : '⛔ Overdue'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fleet Overview */}
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
              <span>📋</span> Fleet Summary
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Vehicle</th>
                    <th className="text-right py-2 px-3 text-slate-400 font-medium">Odometer</th>
                    <th className="text-right py-2 px-3 text-slate-400 font-medium">Next Oil</th>
                    <th className="text-right py-2 px-3 text-slate-400 font-medium">Remaining</th>
                    <th className="text-right py-2 px-3 text-slate-400 font-medium">Services</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v, i) => {
                    const odo = toNum(v.odometerReading);
                    const nextOil = toNum(v.nextOilChangeOdometer);
                    const remaining = nextOil - odo;
                    const unit = v.distanceUnit === 'mi' ? 'mi' : 'km';
                    const vLogs = maintenanceLogs[v._id];
                    const serviceCount = Array.isArray(vLogs) ? vLogs.length : 0;
                    return (
                      <tr key={v._id} className={`border-b border-slate-700/30 ${i % 2 === 0 ? 'bg-slate-800/20' : ''}`}>
                        <td className="py-2 px-3 text-white font-medium">{v.plateNumber} <span className="text-slate-500 font-normal">({v.brand})</span></td>
                        <td className="py-2 px-3 text-right text-slate-300">{odo.toLocaleString()} {unit}</td>
                        <td className="py-2 px-3 text-right text-slate-300">{nextOil > 0 ? `${nextOil.toLocaleString()} ${unit}` : '—'}</td>
                        <td className={`py-2 px-3 text-right font-semibold ${remaining <= 500 ? 'text-red-400' : remaining <= 1000 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {nextOil > 0 ? `${remaining.toLocaleString()} ${unit}` : '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-slate-300">{serviceCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────
const SummaryCard = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl border border-slate-700/30 p-4`}>
    <div className="flex items-center gap-2 mb-1">
      <span>{icon}</span>
      <span className="text-slate-400 text-xs">{label}</span>
    </div>
    <p className="text-white text-lg font-bold">{value}</p>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-10 bg-slate-700/20 rounded-xl border border-slate-700/30">
    <span className="text-3xl mb-2 block">📋</span>
    <p className="text-slate-400 text-sm">No maintenance records yet</p>
    <p className="text-slate-500 text-xs mt-1">Your analytics will appear here after your first service</p>
  </div>
);

export default MaintenanceAnalytics;
